import { BaseTrack, TrackConfig, TrackData } from '../base/base-track';
import { hookRscapeColoring } from '../rscape/rscape-track';

interface SequenceTrackConfig extends TrackConfig {
  label: string;
  dataSource: 'notation' | 'consensus';
  sequenceName?: string;
  /** When true, overlay green on positions where notation.rscape === '2' */
  applyRscapeColor?: boolean;
}

export class SequenceTrack extends BaseTrack {
  private label: string;
  private dataSource: 'notation' | 'consensus';
  private sequenceName: string;
  private applyRscapeColor: boolean;

  constructor(config: SequenceTrackConfig, data: TrackData) {
    super('sequence', { ...config, height: 50 }, data);
    this.label = config.label;
    this.dataSource = config.dataSource;
    this.sequenceName = config.sequenceName || config.label;
    this.applyRscapeColor = config.applyRscapeColor ?? false;
  }

  createHTML(): string {
    if (!this.hasRequiredData() || !this.getSequenceData()) {
      return '';
    }

    const trackId = `${this.dataSource}-sequence-track`;

    return `
      <div style="display: flex; align-items: center; ">
        <div style="width: ${this.config.labelWidth}px; flex-shrink: 0;">
          ${this.label}
        </div>
        <nightingale-sequence
          id="${trackId}">
        </nightingale-sequence>
      </div>
    `;
  }

  setup(container: HTMLElement): void {
    const trackId = `${this.dataSource}-sequence-track`;
    this.element = container.querySelector(`#${trackId}`) as HTMLElement;
    
    if (!this.element) {
      console.error(`${this.label} track element not found`);
      return;
    }

    this.setupSequenceElement();
  }

  update(config?: Partial<SequenceTrackConfig>, data?: Partial<TrackData>): void {
    if (config) {
      this.config = { ...this.config, ...config };
      if (config.label !== undefined) {
        this.label = config.label;
      }
      if (config.dataSource !== undefined) {
        this.dataSource = config.dataSource;
      }
      if (config.sequenceName !== undefined) {
        this.sequenceName = config.sequenceName;
      }
      if ((config as SequenceTrackConfig).applyRscapeColor !== undefined) {
        this.applyRscapeColor = (config as SequenceTrackConfig).applyRscapeColor ?? false;
      }
    }
    if (data) {
      this.data = { ...this.data, ...data };
    }
    
    if (this.element) {
      this.setupSequenceElement();
    }
  }

  destroy(): void {
    this.element = undefined;
  }

  private getSequenceData(): string | undefined {
    switch (this.dataSource) {
      case 'notation':
        return this.data.notation?.consensus;
      case 'consensus':
        return this.data.msaData?.consensus;
      default:
        return undefined;
    }
  }

  private setupSequenceElement(): void {
    const sequenceData = this.getSequenceData();
    if (!this.element || !sequenceData) return;

    const sequence = {
      name: this.sequenceName,
      sequence: sequenceData
    };

    const nightingaleAttributes = this.getNightingaleAttributes();
    this.applyAttributes(this.element, {
      ...nightingaleAttributes,
      'font-size': '14',
      'font-family': 'Courier New, Monaco, Lucida Console, monospace',
      'margin-top': '2',
      'margin-bottom': '2'
    });

    (this.element as any).data = sequence;

    // Overlay green backgrounds where R-scape detected covariation
    if (this.applyRscapeColor && this.data.notation?.rscape) {
      hookRscapeColoring(this.element, this.data.notation.rscape, false /* keep sequence text */);
    }
  }
}